'use client';

import * as React from 'react';
import { Header } from '../Header';
import { FilterSidebar } from '../FilterSidebar/FilterSidebar';
import { Footer } from '../Footer';
import {
  ErrorBoundary,
  Paging,
  WithSearch,
} from '@elastic/react-search-ui';
import '@elastic/react-search-ui-views/lib/styles/styles.css';
import 'rc-slider/assets/index.css';
import './SearchUI.css';
import CustomResults from './CustomResults';
import { SearchRow } from './SearchRow';
import HistoryDrawer from '../HistoryDrawer/HistoryDrawer';
import { useTenant } from '../../context/ThemeContext';
import { RangeState } from '../../types';

interface SearchResultsContentProps {
  wasSearched: boolean;
  results: any[];
  pagingInfo: { totalResults: number };
  current: number;
  resultsPerPage: number;
}

const SearchResultsContent: React.FC<SearchResultsContentProps> = ({
  results,
  current,
  resultsPerPage,
}) => {
  const prevPageRef = React.useRef<number>(1);
  const prevResultsPerPageRef = React.useRef<number>(resultsPerPage);

  React.useEffect(() => {
    const shouldScroll =
      prevPageRef.current !== current ||
      prevResultsPerPageRef.current !== resultsPerPage;
    if (shouldScroll) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      prevPageRef.current = current;
      prevResultsPerPageRef.current = resultsPerPage;
    }
  }, [current, resultsPerPage]);

  return (
    <div className="sui-layout-main flex flex-col flex-1 px-6 pb-6">
      <div className="flex-grow">
        <ErrorBoundary>
          <div className="sui-layout-body flex flex-col">
            <div className="flex-grow">
              {results && results.length > 0 ? (
                <CustomResults results={results} />
              ) : (
                <div className="text-mp-muted text-center py-8 font-mono">
                  &gt; No chassis matching specifications
                </div>
              )}
            </div>
            {results && results.length > 0 && (
              <div className="mt-8 flex justify-center">
                <Paging />
              </div>
            )}
          </div>
        </ErrorBoundary>
      </div>
    </div>
  );
};

export const Body: React.FC = () => {
  const { tenant } = useTenant();
  const [mobileFiltersOpen, setMobileFiltersOpen] = React.useState(false);

  // Range state lifted here so it survives mobile drawer open/close
  const defaultYearRange = tenant.searchConfig?.yearRange || { min: 1989, max: 1999 };
  const defaultHorsePowerRange = tenant.searchConfig?.horsePowerRange || { min: 250, max: 450 };

  const [yearRange, setYearRange] = React.useState<RangeState>(defaultYearRange);
  const [priceRange, setPriceRange] = React.useState<RangeState>({ min: 0, max: 250000 });
  const [mileageRange, setMileageRange] = React.useState<RangeState>({ min: 0, max: 250000 });
  const [horsePowerRange, setHorsePowerRange] = React.useState<RangeState>(defaultHorsePowerRange);

  // Reset ranges when tenant changes
  React.useEffect(() => {
    setYearRange(defaultYearRange);
    setHorsePowerRange(defaultHorsePowerRange);
  }, [tenant.id]);

  // Prevent body scroll when mobile filter drawer is open
  React.useEffect(() => {
    if (mobileFiltersOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileFiltersOpen]);

  const sharedRangeProps = {
    yearRange,
    setYearRange,
    priceRange,
    setPriceRange,
    mileageRange,
    setMileageRange,
    horsePowerRange,
    setHorsePowerRange,
  };

  const mapContextToProps = ({ filters, setFilter, removeFilter, searchTerm, setSearchTerm }: any) => ({
    filters,
    setFilter,
    removeFilter,
    searchTerm,
    setSearchTerm,
    setDateFilter: (field: string, range: { from: string; to: string }) => {
      setFilter(field, {
        from: range.from ? `${range.from}T00:00:00Z` : undefined,
        to: range.to ? `${range.to}T23:59:59Z` : undefined,
      });
    },
    ...sharedRangeProps,
  });

  return (
    <main className="bg-mp-bg min-h-screen flex flex-col max-w-[1920px] mx-auto w-full">
      <Header showFilterChips={true} />

      <div className="flex flex-1">
        {/* Desktop sidebar */}
        <aside className="w-[280px] shrink-0 bg-mp-bg hidden lg:block overflow-y-auto self-start sticky top-[56px] max-h-[calc(100vh-56px)] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <WithSearch mapContextToProps={mapContextToProps}>
            {(contextProps) => <FilterSidebar {...contextProps} />}
          </WithSearch>
        </aside>

        {/* Results column — full width on mobile */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile filter toggle button */}
          <div className="lg:hidden px-4 pt-3 pb-2">
            <button
              className="w-full flex justify-between items-center py-3 px-4 text-mp-text border border-mp-border bg-mp-surface"
              onClick={() => setMobileFiltersOpen(true)}
            >
              <span className="text-sm font-display uppercase tracking-widest">Filters</span>
              <span className="material-symbols-outlined text-mp-muted">tune</span>
            </button>
          </div>

          <SearchRow />
          <WithSearch
            mapContextToProps={({
              wasSearched, results, pagingInfo, current, resultsPerPage,
            }) => ({
              wasSearched: wasSearched || false,
              results: results || [],
              pagingInfo: pagingInfo || { totalResults: 0 },
              current: current || 1,
              resultsPerPage: resultsPerPage || 12,
            })}
          >
            {(props) => <SearchResultsContent {...props} />}
          </WithSearch>
        </div>
      </div>

      {/*
        Mobile filter overlay — always mounted, visibility toggled via CSS.
        alwaysOpen bypasses the internal toggle so all filters are visible immediately.
      */}
      <div
        className={`lg:hidden fixed inset-0 z-50 flex transition-opacity duration-200 ${
          mobileFiltersOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60"
          onClick={() => setMobileFiltersOpen(false)}
        />

        {/* Drawer */}
        <div
          className={`relative w-[85vw] max-w-[320px] bg-mp-bg h-full overflow-y-auto flex flex-col transition-transform duration-200 ${
            mobileFiltersOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex justify-between items-center px-4 pt-4 pb-2 border-b border-mp-border">
            <span className="text-sm font-display uppercase tracking-widest text-mp-text">Filters</span>
            <button
              onClick={() => setMobileFiltersOpen(false)}
              className="text-mp-muted hover:text-mp-text transition-colors"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          <WithSearch mapContextToProps={mapContextToProps}>
            {(contextProps) => <FilterSidebar {...contextProps} alwaysOpen />}
          </WithSearch>
        </div>
      </div>

      <HistoryDrawer />
      <Footer />
    </main>
  );
};

export default Body;
